export default class Matrix4 {

	private data: Float32Array;

	private constructor (data: Float32Array | number[]) {
		this.data = new Float32Array(data);
	}

	public static create (data: number[]) {
		return new Matrix4(data);
	}

	public static identity () {
		return new Matrix4([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]);
	}

	/*
	 * Command methods
	 */

	public rotateX (radians: number) {
		const sin = Math.sin(radians);
		const cos = Math.cos(radians);

		return this.rightMultiply(new Matrix4([1, 0, 0, 0, 0, cos, sin, 0, 0, -sin, cos, 0, 0, 0, 0, 1]));
	}

	public rotateY (radians: number) {
		const sin = Math.sin(radians);
		const cos = Math.cos(radians);

		return this.rightMultiply(new Matrix4([cos, 0, -sin, 0, 0, 1, 0, 0, sin, 0, cos, 0, 0, 0, 0, 1]));
	}

	public rotateZ (radians: number) {
		const sin = Math.sin(radians);
		const cos = Math.cos(radians);

		return this.rightMultiply(new Matrix4([cos, sin, 0, 0, -sin, cos, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]));
	}

	public scale (x: number, y: number, z: number) {
		return this.rightMultiply(new Matrix4([x, 0, 0, 0, 0, y, 0, 0, 0, 0, z, 0, 0, 0, 0, 1]));
	}

	public scaleX (x: number) {
		return this.scale(x, 1, 1);
	}

	public scaleY (y: number) {
		return this.scale(1, y, 1);
	}

	public scaleZ (z: number) {
		return this.scale(1, 1, z);
	}

	public translate (x: number, y: number, z: number) {
		return this.rightMultiply(new Matrix4([1, 0, 0, x, 0, 1, 0, y, 0, 0, 1, z, 0, 0, 0, 1]));
	}

	public translateX (x: number) {
		return this.translate(x, 0, 0);
	}

	public translateY (y: number) {
		return this.translate(0, y, 0);
	}

	public translateZ (z: number) {
		return this.translate(0, 0, z);
	}

	public transpose () {
		const data = this.data;

		const d1 = data[1];
		const d2 = data[2];
		const d3 = data[3];
		const d6 = data[6];
		const d7 = data[7];
		const d11 = data[11];

		data[1] = data[4];
		data[2] = data[8];
		data[3] = data[12];
		data[6] = data[9];
		data[7] = data[13];
		data[11] = data[14];

		data[4] = d1;
		data[8] = d2;
		data[12] = d3;
		data[9] = d6;
		data[13] = d7;
		data[14] = d11;

		return this;
	}

	/*
	 * Query methods
	 */

	public copy () {
		return new Matrix4(this.data);
	}

	public getData () {
		return this.data;
	}

	/*
	 * Private methods
	 */

	private leftMultiply (rightMatrix: Matrix4) {
		const d = this.data;
		const r = rightMatrix.data;

		const d0 = d[0]; const d1 = d[1]; const d2 = d[2]; const d3 = d[3];
		const d4 = d[4]; const d5 = d[5]; const d6 = d[6]; const d7 = d[7];
		const d8 = d[8]; const d9 = d[9]; const d10 = d[10]; const d11 = d[11];
		const d12 = d[12]; const d13 = d[13]; const d14 = d[14]; const d15 = d[15];

		d[0] = d0 * r[0] + d1 * r[4] + d2 * r[8] + d3 * r[12];
		d[1] = d0 * r[1] + d1 * r[5] + d2 * r[9] + d3 * r[13];
		d[2] = d0 * r[2] + d1 * r[6] + d2 * r[10] + d3 * r[14];
		d[3] = d0 * r[3] + d1 * r[7] + d2 * r[11] + d3 * r[15];

		d[4] = d4 * r[0] + d5 * r[4] + d6 * r[8] + d7 * r[12];
		d[5] = d4 * r[1] + d5 * r[5] + d6 * r[9] + d7 * r[13];
		d[6] = d4 * r[2] + d5 * r[6] + d6 * r[10] + d7 * r[14];
		d[7] = d4 * r[3] + d5 * r[7] + d6 * r[11] + d7 * r[15];

		d[8] = d8 * r[0] + d9 * r[4] + d10 * r[8] + d11 * r[12];
		d[9] = d8 * r[1] + d9 * r[5] + d10 * r[9] + d11 * r[13];
		d[10] = d8 * r[2] + d9 * r[6] + d10 * r[10] + d11 * r[14];
		d[11] = d8 * r[3] + d9 * r[7] + d10 * r[11] + d11 * r[15];

		d[12] = d12 * r[0] + d13 * r[4] + d14 * r[8] + d15 * r[12];
		d[13] = d12 * r[1] + d13 * r[5] + d14 * r[9] + d15 * r[13];
		d[14] = d12 * r[2] + d13 * r[6] + d14 * r[10] + d15 * r[14];
		d[15] = d12 * r[3] + d13 * r[7] + d14 * r[11] + d15 * r[15];

		return this;
	}

	private rightMultiply (leftMatrix: Matrix4) {
		const d = this.data;
		const l = leftMatrix.data;

		const d0 = d[0]; const d1 = d[1]; const d2 = d[2]; const d3 = d[3];
		const d4 = d[4]; const d5 = d[5]; const d6 = d[6]; const d7 = d[7];
		const d8 = d[8]; const d9 = d[9]; const d10 = d[10]; const d11 = d[11];
		const d12 = d[12]; const d13 = d[13]; const d14 = d[14]; const d15 = d[15];

		d[0] = l[0] * d0 + l[1] * d4 + l[2] * d8 + l[3] * d12;
		d[1] = l[0] * d1 + l[1] * d5 + l[2] * d9 + l[3] * d13;
		d[2] = l[0] * d2 + l[1] * d6 + l[2] * d10 + l[3] * d14;
		d[3] = l[0] * d3 + l[1] * d7 + l[2] * d11 + l[3] * d15;

		d[4] = l[4] * d0 + l[5] * d4 + l[6] * d8 + l[7] * d12;
		d[5] = l[4] * d1 + l[5] * d5 + l[6] * d9 + l[7] * d13;
		d[6] = l[4] * d2 + l[5] * d6 + l[6] * d10 + l[7] * d14;
		d[7] = l[4] * d3 + l[5] * d7 + l[6] * d11 + l[7] * d15;

		d[8] = l[8] * d0 + l[9] * d4 + l[10] * d8 + l[11] * d12;
		d[9] = l[8] * d1 + l[9] * d5 + l[10] * d9 + l[11] * d13;
		d[10] = l[8] * d2 + l[9] * d6 + l[10] * d10 + l[11] * d14;
		d[11] = l[8] * d3 + l[9] * d7 + l[10] * d11 + l[11] * d15;

		d[12] = l[12] * d0 + l[13] * d4 + l[14] * d8 + l[15] * d12;
		d[13] = l[12] * d1 + l[13] * d5 + l[14] * d9 + l[15] * d13;
		d[14] = l[12] * d2 + l[13] * d6 + l[14] * d10 + l[15] * d14;
		d[15] = l[12] * d3 + l[13] * d7 + l[14] * d11 + l[15] * d15;

		return this;
	}
}


// WEBPACK FOOTER //
// ./code/foundation/algebra/linear/Matrix4.ts